import React, { useState, useEffect } from "react";
import { X, Menu } from "lucide-react";
import KanactfyLogo from "./ClientLogos/kanactifyLogo.jpeg";

/* ─────────────────────────────────────────────────────
   NAVBAR CSS
───────────────────────────────────────────────────── */
const NAV_CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

/* ── NAV BASE ── */
.kn-nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 999;
  padding: 18px 0;
  transition: background .4s cubic-bezier(.4,0,.2,1),
              padding .4s cubic-bezier(.4,0,.2,1),
              box-shadow .4s cubic-bezier(.4,0,.2,1);
}
.kn-nav.scrolled {
  background: rgba(255, 252, 249, 0.97);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  padding: 11px 0;
  box-shadow:
    0 1px 0 rgba(248,108,61,.1),
    0 8px 40px rgba(248,108,61,.06),
    0 2px 16px rgba(0,0,0,.04);
}

/* ── INNER ── */
.kn-nav__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

/* ── LOGO ── */
.kn-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex-shrink: 0;
}
.kn-logo__img {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid rgba(248,108,61,.22);
  transition: transform .3s cubic-bezier(.34,1.56,.64,1), box-shadow .3s;
}
.kn-logo:hover .kn-logo__img {
  transform: scale(1.08) rotate(-2deg);
  box-shadow: 0 6px 20px rgba(248,108,61,.28);
}
.kn-logo__text {
  font-family: "DM Sans", sans-serif;
  font-size: 19px;
  font-weight: 800;
  color: #0A0A0A;
  letter-spacing: -.2px;
}
.kn-logo__text span { color: #F86C3D; }

/* ── DESKTOP LINKS ── */
.kn-nav__links {
  display: flex;
  align-items: center;
  gap: 36px;
  flex: 1;
  justify-content: center;
}
.kn-nav__links a {
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #705848;
  text-decoration: none;
  position: relative;
  padding: 4px 0;
  transition: color .2s;
  white-space: nowrap;
}
.kn-nav__links a::after {
  content: "";
  position: absolute;
  bottom: -2px; left: 0; right: 0;
  height: 1.5px;
  background: linear-gradient(90deg, #F86C3D, #FF8A5C);
  transform: scaleX(0);
  transform-origin: right;
  transition: transform .3s cubic-bezier(.4,0,.2,1);
  border-radius: 2px;
}
.kn-nav__links a:hover { color: #0A0A0A; }
.kn-nav__links a:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}
.kn-nav__links a.active { color: #D9572E; }
.kn-nav__links a.active::after { transform: scaleX(1); }

/* ── CTA BUTTON ── */
.kn-nav__cta {
  flex-shrink: 0;
  padding: 11px 26px;
  border-radius: 100px;
  background: linear-gradient(135deg, #F86C3D, #D9572E);
  color: #fff;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(248,108,61,.32);
  transition: transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .25s;
  white-space: nowrap;
}
.kn-nav__cta:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 12px 32px rgba(248,108,61,.45);
}
.kn-nav__cta:active { transform: scale(.97); }

/* ── HAMBURGER ── */
.kn-nav__burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  background: none;
  border: 1.5px solid rgba(248,108,61,.2);
  border-radius: 10px;
  cursor: pointer;
  padding: 8px;
  flex-shrink: 0;
  transition: border-color .2s, background .2s;
}
.kn-nav__burger:hover {
  border-color: rgba(248,108,61,.5);
  background: rgba(248,108,61,.04);
}
.kn-nav__burger span {
  width: 100%;
  height: 1.5px;
  background: #0A0A0A;
  border-radius: 2px;
  display: block;
  transition: transform .3s, opacity .3s, width .3s;
  transform-origin: center;
}
.kn-nav__burger.open span:nth-child(1) {
  transform: translateY(6.5px) rotate(45deg);
}
.kn-nav__burger.open span:nth-child(2) {
  opacity: 0;
  width: 0;
}
.kn-nav__burger.open span:nth-child(3) {
  transform: translateY(-6.5px) rotate(-45deg);
}

/* ── PROGRESS BAR ── */
.kn-nav__progress {
  position: absolute;
  bottom: 0; left: 0;
  height: 2px;
  background: linear-gradient(90deg, #F86C3D, #FF8A5C, #F86C3D);
  background-size: 200% 100%;
  animation: progressShimmer 2s linear infinite;
  transition: width .1s linear;
  border-radius: 0 2px 2px 0;
}
@keyframes progressShimmer {
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
}

/* ── MOBILE MENU OVERLAY ── */
.kn-mobile-menu {
  position: fixed;
  inset: 0;
  z-index: 998;
  display: flex;
  flex-direction: column;
  opacity: 0;
  pointer-events: none;
  transition: opacity .35s cubic-bezier(.4,0,.2,1);
}
.kn-mobile-menu.open {
  opacity: 1;
  pointer-events: all;
}
.kn-mobile-menu__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10,8,6,.6);
  backdrop-filter: blur(4px);
}
.kn-mobile-menu__panel {
  position: absolute;
  top: 0; right: 0;
  width: min(360px, 100vw);
  height: 100%;
  background: #FFFCF9;
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform .4s cubic-bezier(.77,0,.175,1);
  box-shadow: -24px 0 80px rgba(248,108,61,.12);
  overflow-y: auto;
}
.kn-mobile-menu.open .kn-mobile-menu__panel {
  transform: translateX(0);
}

/* panel header */
.kn-mob__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-bottom: 1px solid rgba(248,108,61,.1);
  flex-shrink: 0;
}
.kn-mob__close {
  width: 38px; height: 38px;
  border-radius: 10px;
  border: 1.5px solid rgba(248,108,61,.2);
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #705848;
  transition: background .2s, color .2s;
}
.kn-mob__close:hover {
  background: rgba(248,108,61,.08);
  color: #F86C3D;
}

/* panel nav links */
.kn-mob__links {
  flex: 1;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.kn-mob__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-radius: 14px;
  font-family: "DM Sans", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #705848;
  text-decoration: none;
  transition: background .2s, color .2s, transform .2s;
}
.kn-mob__link:hover, .kn-mob__link.active {
  background: rgba(248,108,61,.07);
  color: #D9572E;
  transform: translateX(4px);
}
.kn-mob__link span {
  font-size: 12px;
  opacity: 0;
  transition: opacity .2s;
}
.kn-mob__link:hover span, .kn-mob__link.active span {
  opacity: 1;
}

/* panel footer */
.kn-mob__footer {
  padding: 20px 28px 36px;
  flex-shrink: 0;
  border-top: 1px solid rgba(248,108,61,.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.kn-mob__cta {
  width: 100%;
  padding: 15px;
  border-radius: 14px;
  background: linear-gradient(135deg, #F86C3D, #D9572E);
  color: #fff;
  font-family: "DM Sans", sans-serif;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(248,108,61,.35);
  transition: transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .25s;
}
.kn-mob__cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 36px rgba(248,108,61,.45);
}
.kn-mob__contact {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #9A8070;
  text-decoration: none;
}
.kn-mob__contact:hover { color: #F86C3D; }

/* ── RESPONSIVE ── */
@media (max-width: 1024px) {
  .kn-nav__inner { padding: 0 32px; gap: 20px; }
  .kn-nav__links { gap: 24px; }
}
@media (max-width: 768px) {
  .kn-nav__links { display: none; }
  .kn-nav__cta { display: none; }
  .kn-nav__burger { display: flex; }
  .kn-nav__inner { padding: 0 20px; }
}
@media (max-width: 480px) {
  .kn-nav__inner { padding: 0 16px; }
}
`;

/* ─────────────────────────────────────────────────────
   NAV LINKS CONFIG
───────────────────────────────────────────────────── */
const NAV_LINKS = [
  { id: "home",     label: "Home" },
  { id: "services", label: "Services" },
  { id: "stories",  label: "Case Studies" },
  { id: "clients",  label: "Clients" },
  { id: "about",    label: "About" },
];

/* ─────────────────────────────────────────────────────
   NAVBAR COMPONENT
───────────────────────────────────────────────────── */
const Navbar = () => {
  const [scrolled, setScrolled]     = useState(false);
  const [progress, setProgress]     = useState(0);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [activeSection, setActive]  = useState("home");

  /* scroll listener — progress bar + stuck state + active section */
  useEffect(() => {
    const onScroll = () => {
      const scrollY   = window.scrollY;
      const maxScroll = document.body.scrollHeight - window.innerHeight;

      setScrolled(scrollY > 60);
      setProgress(maxScroll > 0 ? (scrollY / maxScroll) * 100 : 0);

      /* detect active section */
      const offsets = NAV_LINKS.map(({ id }) => {
        const el = document.getElementById(id);
        return el ? { id, top: el.getBoundingClientRect().top } : null;
      }).filter(Boolean);

      const current = offsets
        .filter(o => o.top <= 120)
        .sort((a, b) => b.top - a.top)[0];

      if (current) setActive(current.id);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* lock body scroll when mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const goTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMenuOpen(false);
  };

  return (
    <>
      <style>{NAV_CSS}</style>

      {/* ── DESKTOP NAV ── */}
      <nav className={"kn-nav" + (scrolled ? " scrolled" : "")}>
        <div className="kn-nav__inner">

          {/* Logo */}
          <a href="#home" className="kn-logo" onClick={e => { e.preventDefault(); goTo("home"); }}>
            <img src={KanactfyLogo} alt="Kanactify" className="kn-logo__img" />
            <span className="kn-logo__text">Kan<span>actify</span></span>
          </a>

          {/* Links */}
          <div className="kn-nav__links">
            {NAV_LINKS.map(({ id, label }) => (
              <a
                key={id}
                href={"#" + id}
                className={activeSection === id ? "active" : ""}
                onClick={e => { e.preventDefault(); goTo(id); }}
              >
                {label}
              </a>
            ))}
          </div>

          {/* CTA */}
          <button className="kn-nav__cta" onClick={() => goTo("contact")}>
            Get Started
          </button>

          {/* Hamburger */}
          <button
            className={"kn-nav__burger" + (menuOpen ? " open" : "")}
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>

        {/* Scroll progress bar */}
        {scrolled && (
          <div
            className="kn-nav__progress"
            style={{ width: progress + "%" }}
          />
        )}
      </nav>

      {/* ── MOBILE SLIDE-IN PANEL ── */}
      <div
        className={"kn-mobile-menu" + (menuOpen ? " open" : "")}
        onClick={e => { if (e.target === e.currentTarget) setMenuOpen(false); }}
      >
        <div className="kn-mobile-menu__backdrop" onClick={() => setMenuOpen(false)} />

        <div className="kn-mobile-menu__panel">
          {/* Header */}
          <div className="kn-mob__header">
            <a href="#home" className="kn-logo" onClick={e => { e.preventDefault(); goTo("home"); }}>
              <img src={KanactfyLogo} alt="Kanactify" className="kn-logo__img" />
              <span className="kn-logo__text">Kan<span>actify</span></span>
            </a>
            <button className="kn-mob__close" onClick={() => setMenuOpen(false)}>
              <X size={18} />
            </button>
          </div>

          {/* Links */}
          <div className="kn-mob__links">
            {NAV_LINKS.map(({ id, label }, i) => (
              <a
                key={id}
                href={"#" + id}
                className={"kn-mob__link" + (activeSection === id ? " active" : "")}
                style={{ transitionDelay: menuOpen ? i * 40 + "ms" : "0ms" }}
                onClick={e => { e.preventDefault(); goTo(id); }}
              >
                {label}
                <span>→</span>
              </a>
            ))}
          </div>

          {/* Footer */}
          <div className="kn-mob__footer">
            <button className="kn-mob__cta" onClick={() => goTo("contact")}>
              Get Started — It's Free
            </button>
            <a href="tel:+919021076873" className="kn-mob__contact">
              📞 +91 9021076873
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;